import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  CircularProgress,
  Checkbox
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import AccountCircleIcon from '@material-ui/icons/AccountCircle';

import RenderFieldBasedOnType from '../RenderFieldsBasedOnType';
import TaskCommentViewer from './TaskComments';

import {
  handleCommonMasterDataConfigOnLoad,
  updateCommonMasterDataResponseToComponent,
  Helpers
} from 'dsilo-ui-components';

import Avatar from '@material-ui/core/Avatar';
/* For now It is static */
const defaultOptions = {};

const useStyles = makeStyles((theme) => ({
  /** Dialog paper + responsive width/height */
  customDialogPaper: {
    width: '90vw',
    maxWidth: 900,
    maxHeight: '90vh',
    border: '1px solid rgba(224, 224, 224, 1)',
    borderRadius: 8,
    overflow: 'hidden',        // let content area handle scroll
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
    },
  },

  commentAvatar: {
    height: 24,
    width: 24,
    fontSize: 11,
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'normal',
    backgroundColor: '#5398E6',
    color: '#fff',
  },

  customDialogPaperSuccess: {
    width: '90vw',
    maxWidth: 300, // Smaller width for success
    maxHeight: '90vh',
    border: '1px solid rgba(224, 224, 224, 1)',
    borderRadius: 8,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
    },
  },

  /** Custom width for rejected state */
  customDialogPaperRejected: {
    width: '90vw',
    maxWidth: 300, // Slightly larger width for rejected
    maxHeight: '90vh',
    border: '1px solid rgba(224, 224, 224, 1)',
    borderRadius: 8,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
    },
  },

  customDialogPaperforwardTo: {
    width: '90vw',
    maxWidth: 428, // Slightly larger width for rejected
    maxHeight: '90vh',
    border: '1px solid rgba(224, 224, 224, 1)',
    borderRadius: 8,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
    },
  },

  customDialogPaperDeleteTask: {
    width: '90vw',
    maxWidth: 350, // Slightly larger width for rejected
    maxHeight: '90vh',
    border: '1px solid rgba(224, 224, 224, 1)',
    borderRadius: 8,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
    },
  },

  /** Scroll only the content area */
  dialogContent: {
    padding: theme.spacing(2),
    overflowY: 'auto',
    maxHeight: 'calc(90vh - 120px)', // title + actions reserved
    [theme.breakpoints.down('sm')]: {
      maxHeight: 'calc(100vh - 120px)',
      padding: theme.spacing(1.5),
    },
  },

  /** Inputs */
  inputField: { width: '100%', height: 35, paddingLeft: 5 },
  smallOutlinedInput: {
    fontSize: 12,
    height: 32,
    padding: '5px 8px 6px 8px',
    '& input': { padding: '5px 4px 6px 4px', fontSize: 12 },
  },
  smallNotchedOutline: { fontSize: 12, borderColor: '#ccc' },
  noScroll: { overflowY: 'visible' },

  /** Typography */
  dialogTitleText: {
    fontWeight: 600,
    fontSize: 14,
    color: 'rgba(33, 33, 33, 1)',
    [theme.breakpoints.down('xs')]: { fontSize: 13 },
  },
  labelText: {
    fontWeight: 400,
    fontSize: 12,
    color: 'rgba(117, 117, 117, 1)',
    [theme.breakpoints.down('xs')]: { fontSize: 11 },
  },
  centerSubText: {
    fontWeight: 400,
    fontSize: 12,
    color: 'rgba(97, 97, 97, 1)',
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: { fontSize: 11 },
  },
  infoText: {
    fontWeight: 400,
    fontSize: 14,
    color: 'rgba(33, 33, 33, 1)',
    [theme.breakpoints.down('xs')]: { fontSize: 13 },
  },
  auditMessage: {
    fontSize: 12,
    fontWeight: 400,
    color: 'rgba(43, 44, 53, 1)',
    flexGrow: 1,
    textOverflow: 'ellipsis',
    maxWidth: 190,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: { maxWidth: 140 },
    [theme.breakpoints.down('xs')]: { maxWidth: 110, fontSize: 11 },
  },
  auditBy: {
    fontSize: 10,
    fontWeight: 400,
    color: 'rgba(95, 96, 113, 1)',
    flexGrow: 1,
    [theme.breakpoints.down('xs')]: { fontSize: 9 },
  },

  /** Flex helpers */
  rowStartCenter: { display: 'flex', justifyContent: 'start', alignItems: 'center' },
  col: { display: 'flex', flexDirection: 'column' },
  gap4: { gap: 4 },
  gap5: { gap: 5 },
  gap8: { gap: 8 },
  gap10: { gap: 10 },
  gap12: { gap: 12 },
  gap16: { gap: 16 },
  gap24: { gap: 24 },

  /** Buttons */
  btnPrimary: {
    backgroundColor: 'rgba(2, 136, 209, 1)',
    color: '#fff',
    borderRadius: 4,
    textTransform: 'none',
    '&:hover': { backgroundColor: 'rgba(2, 136, 209, 0.9)' },
  },
  btnDanger: {
    backgroundColor: 'rgba(239, 108, 0, 1)',
    color: '#fff',
    borderRadius: 4,
    textTransform: 'none',
    '&:hover': { backgroundColor: 'rgba(239, 108, 0, 0.9)' },
  },
  btnSuccess: {
    backgroundColor: 'rgba(46, 125, 50, 1)',
    color: '#fff',
    height: 30,
    borderRadius: 4,
    textTransform: 'none',
    '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.9)' },
  },
  btnSmallTall: {
    height: 36,
    padding: '6px 16px',
    fontSize: 12,
    [theme.breakpoints.down('xs')]: { height: 32, padding: '6px 12px', fontSize: 11 },
  },
  btnSmallCompact: {
    height: 25,
    padding: '9px 6px',
    fontSize: 12,
    [theme.breakpoints.down('xs')]: { height: 24, padding: '6px 6px', fontSize: 11 },
  },
  btnOutlinedNeutral: { color: 'rgba(0, 0, 0, 0.87)', padding: 10, textTransform: 'none', height: 30 },

  /** Tabs */
  tabsBar: {
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    borderBottom: '1px solid rgba(198, 199, 209, 1)',
    gap: 15,
    [theme.breakpoints.down('xs')]: { gap: 10 },
  },
  tab: { display: 'flex', gap: 12, padding: 6, cursor: 'pointer', borderBottom: '1px solid transparent' },
  tabActive: { borderBottom: '1px solid rgba(63, 81, 181, 1)', marginBottom: -1 },
  tabText: {
    fontSize: 14,
    fontWeight: 400,
    color: 'rgba(120, 121, 142, 1)',
    [theme.breakpoints.down('xs')]: { fontSize: 13 },
  },
  tabTextActive: { color: 'rgba(63, 81, 181, 1)' },

  /** Success/Reject/Delete content wrappers */
  dialogCenterWrap: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: 24,
    gap: 16,
    [theme.breakpoints.down('xs')]: { padding: 16, gap: 12 },
  },
  centerRow: { display: 'flex', justifyContent: 'center', alignItems: 'center' },

  /** Icons / images */
  icon48: { width: 48, height: 48, [theme.breakpoints.down('xs')]: { width: 40, height: 40 } },
  icon20Btn: {
    width: 20,
    height: 20,
    padding: 5,
    borderRadius: 50,
    border: '1px solid rgba(233, 235, 245, 1)',
    backgroundColor: 'rgba(250, 250, 254, 1)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  accountIconGreen: { color: 'rgba(48, 164, 97, 1)', height: 24, width: 24 },

  /** Audit trails container (scrollable) */
  auditScroll: {
    border: '1px solid rgba(233, 235, 245, 1)',
    borderRadius: 4,
    padding: 5,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    overflowY: 'auto',
    maxHeight: 300,
    marginTop: 10,
    [theme.breakpoints.down('xs')]: { maxHeight: 240 },
  },
  auditRow: { height: 44, backgroundColor: '#fff', gap: 16, display: 'flex', padding: '10px 5px' },
  auditDateCol: {
    display: 'flex',
    flexDirection: 'column',
    width: 150,
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: { width: 120 },
    [theme.breakpoints.down('xs')]: { width: 100 },
  },
  auditDateText: { fontSize: 12, fontWeight: 400, color: 'rgba(95, 96, 113, 1)' },
  auditIconCol: { width: 32, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  auditMsgCol: { display: 'flex', flexDirection: 'column', alignItems: 'start', justifyContent: 'start', flexGrow: 1 },

  /** Grid + Box helpers */
  boxFieldStack: { width: '100%', display: 'flex', flexDirection: 'column', gap: 5 },
  mtHalf: { marginTop: theme.spacing(0.5) },
  mt10: { marginTop: 10 },
}));

const SubDataModelModal = (props) => {
  const {
    appId, members, modalInfo, block, formHeaders,
    onSaveHandler, currentOpportunityId, selectedStep, chartUniqueId,
    formData, loader, dataModel, classes, onCloseModel, onDeleteTask
  } = props;
  const { document } = formData;

  const inputClasses = useStyles();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  let payloadForMasterData = {
    docId: currentOpportunityId,
    appId: appId,
    dataUniqueId: chartUniqueId,
    dmId: block.dataModel,
  };

  const [modalFormState, setModalFormState] = useState({});
  const [modifiedFields, setModifiedFields] = useState({});
  const [selectedId, setSelectedId] = useState('');

  const [commonMasterDataConfigData, setCommonMasterDataConfigData] = useState(null);
  const [commonMasterDataConfiguredElements, setCommonMasterDataConfiguredElements] = useState([]);
  const [commonMasterDataFullDropDownKeys, setCommonMasterDataFullDropDownKeys] = useState([]);

  const [masterDataStoreObject, setMasterDataStoreObject] = useState({});
  const [commentTypeFieldState, setCommentTypeFieldState] = useState("");
  const [currentButtonClicked, setCurrentButtonClicked] = useState('');
  const [defaultOptionsDropdown, setDefaultOptionsDropdown] = useState([]);
  const [defaultOptionsDropdownForward, setDefaultOptionsDropdownForward] = useState([]);
  const [firstStepsMembersDropdown, setFirstStepsMembersDropdown] = useState([]);
  const [defaultNextRespondent, setDefaultNextRespondent] = useState([]);
  const [currentTaskAssigneName, setCurrentTaskAssigneName] = useState(null);
  const [defaultNextRespondentForward, setDefaultNextRespondentForward] = useState([]);

  const [onStateChangeTrack, setOnStateChangeTrack] = useState(false);
  const [onAssignedToChangeTrack, setOnAssignedToChangeTrack] = useState(false);
  const [onActions, setOnActions] = useState(false);

  const [activeTabMain, setActiveTabMain] = useState("taskDetails");
  const [selectedAssigneeId, setSelectedAssigneeId] = useState({});
  const [auditTrail, setAuditTrail] = useState([]);
  const [isNextStepEnd, setIsNextStepEnd] = useState(false);

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  useEffect(() => {
    if (modalInfo.enable) {
      if (modalInfo.type === 'edit' && modalInfo.data?._id) {
        setSelectedId(modalInfo.data._id);
        getDataById(modalInfo.data._id);
      }
      if (modalInfo.type === 'view' && modalInfo.data?._id) {
        setSelectedId(modalInfo.data._id);
        getDataById(modalInfo.data._id);
      }
      if (modalInfo.type === 'create') {
        let d = document?.[`${props.data.chartUniqueId}_Task_First_Step_Members`];
        setFirstStepsMembersDropdown(d);
        const formattedWorkflow = d?.map(item => ({
          value: item.name,
          label: item.name,
        }));
        setDefaultOptionsDropdown(formattedWorkflow);
      }
      if (modalInfo.type === 'forwardTo') {
        setSelectedId(modalInfo.data._id);
        getDataById(modalInfo.data._id);
      }
    }
  }, [modalInfo]);

  const checkIfNextStepIsEnd = (mfs) => {
    const steps = props.formData?.document?.[`${props.data.chartUniqueId}_Tasks_Workflow`]?.diagram?.steps || [];
    console.log("steps**", steps, mfs?.workflow)
    const currentStep = mfs?.workflow;
    if (!currentStep || steps.length === 0) {
      setIsNextStepEnd(false);
      return;
    }
    const currentStepIndex = steps.findIndex(s => s.id === currentStep?.id);
    const nextStepType = currentStepIndex >= 0 && steps[currentStepIndex + 2]?.type;
    console.log("steps*nextStepType", nextStepType)
    setIsNextStepEnd(nextStepType === "end");
  };

  useEffect(() => {
    if (props.formData?.document?.[`${props.data.chartUniqueId}_Current_Steps_Members_Task`]) {
      let d = props.formData?.document?.[`${props.data.chartUniqueId}_Current_Steps_Members_Task`];
      setDefaultNextRespondentForward(d);
      const formattedWorkflow = d
        ?.filter(item => item.name !== currentTaskAssigneName)
        .map(item => ({ value: item.name, label: item.name }));
      setDefaultOptionsDropdownForward(formattedWorkflow);
      props.clearReduxDataOfCurrentComponent({
        key: 'document',
        subKey: [`${chartUniqueId}_${block.name}_Current_Steps_Members_Task`],
      });
    }
  }, [props.formData?.document?.[`${props.data.chartUniqueId}_Current_Steps_Members_Task`]]);

  const currentStepMembersForobligation = (obj) => {
    let payload = {
      dataUniqueId: `${props.data.chartUniqueId}_Current_Steps_Members_Task`,
      currentStepsId: obj["workflow"].id,
      payload: { workFlowId: block.workFlowId },
      appId: props.match.params.appid,
    };
    setCurrentTaskAssigneName(obj["workflow"]?.currentRespondent?.name);
    props.currentStepMembersForobligation(payload);
  };

  const getAuditTrailsData = (id) => {
    props.getAuditTrailsData({
      dataModelId: block.dataModel,
      appId: props.match.params.appid,
      documentId: id,
      dataUniqueId: chartUniqueId + '_audit_Trails_Task_Obligations',
    });
  };

  useEffect(() => {
    if (props.formData?.auditTrails?.[chartUniqueId + '_audit_Trails_Task_Obligations']) {
      let obj = props.formData?.auditTrails[chartUniqueId + '_audit_Trails_Task_Obligations'] || {};
      setAuditTrail(obj);
    }
  }, [props.formData?.auditTrails?.[chartUniqueId + '_audit_Trails_Task_Obligations']]);

  const getOperationIcon = (formName, operation, formType) => {
    const operationIcons = {
      Created: '/media/svg/AddTaskObligationAudit.svg',
      taskCreated: '/media/svg/AddTaskObligationAudit.svg',
      Accept: '/media/svg/tickObligations.svg',
      "Assign document": '/media/svg/assignedObligationAudit.svg',
      assigned: '/media/svg/assignedObligationAudit.svg',
      deleted: '/media/svg/deleteTaskAudit.svg',
      taskUpdated: '/media/svg/editObligationAudit.svg',
      forwarded: '/media/svg/forwardObligationAudit.svg',
      messaged: '/media/svg/messageObligationAudit.svg',
      Modify: '/media/svg/moveObligationsAudit.svg',
      rejectObligation: '/media/svg/tickObligations.svg',
      taskDelete: '/media/svg/deleteTaskAudit.svg',
    };
    if (operation === 'Modify' && formType === "Reject") return operationIcons["rejectObligation"];
    if (formName === "workflow" && formType === "Comment") return operationIcons["messaged"];
    return operationIcons[operation];
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const formatted = date.toLocaleString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return formatted.replace(/\//g, "-");
  };

  useEffect(() => {
    if (!props.viewMode && formHeaders.length > 0 && dataModel) {
      handleCommonMasterDataConfigOnLoad(
        formHeaders,
        dataModel,
        {},
        setCommonMasterDataConfigData,
        setCommonMasterDataConfiguredElements,
        payloadForMasterData,
        props.getModelData,
        props.fetchmasterDataForDataelementUsingRules,
        '',
        props.formData
      );
    }
  }, [props.viewMode, dataModel]);

  useEffect(() => {
    if (!props.viewMode && commonMasterDataConfigData) {
      updateCommonMasterDataResponseToComponent(
        props,
        false,
        commonMasterDataConfigData,
        commonMasterDataFullDropDownKeys,
        chartUniqueId,
        document,
        '',
        modalFormState,
        [],
        modifiedFields,
        masterDataStoreObject,
        setModalFormState,
        null,
        null,
        null,
        null,
        setModifiedFields,
        setMasterDataStoreObject
      );
    }
  }, [JSON.stringify(document)]);

  useEffect(() => {
    if (document?.[`${chartUniqueId}_${block.name}_Create_Task`]) {
      onCloseModel();
      toast.success(document?.[`${chartUniqueId}_${block.name}_Create_Task`].message);
      props.clearReduxDataOfCurrentComponent({
        key: 'document',
        subKey: [`${chartUniqueId}_${block.name}_Create_Task`],
      });
      props.clearReduxDataOfCurrentComponent({
        key: 'document',
        subKey: [`${chartUniqueId}_${block.name}_Task_First_Step_Members`],
      });
      props.clearReduxDataOfCurrentComponent({
        key: 'document',
        subKey: [`${chartUniqueId}_${block.name}_All_Task_Data`],
      });
      onSaveHandler();
    }
  }, [document?.[`${chartUniqueId}_${block.name}_Create_Task`]]);

  useEffect(() => {
    if (document?.[`${chartUniqueId}_${block.name}_${selectedId}_Task`]) {
      getAuditTrailsData(modalInfo.data._id);
      let d = document?.[`${chartUniqueId}_${block.name}_${selectedId}_Task`];
      let dn = { ...d };
      if (d.workflow) {
        dn = { ...dn, Currentassignee: d.workflow.currentRespondent.name };
        currentStepMembersForobligation(dn);
        checkIfNextStepIsEnd(dn);
        fetchTasks(dn);
      }
      if (d?.actions) setOnActions(d?.actions?.length ? true : false);
      setModalFormState(dn);
    }
  }, [document?.[`${chartUniqueId}_${block.name}_${selectedId}_Task`]]);

  const handleOnChange = (name, value, _u, uiFieldType, header, valueObj) => {
    setOnStateChangeTrack(true);
    let v = value;
    if (header?.type === "namepicker" && valueObj) {
      v = { id: valueObj?._id, name: valueObj?.name };
    }
    if (header?.type === "namepicker" && header.data.type === 'roles' && valueObj) {
      v = valueObj?.name;
    }
    if (header?.type === "comments") {
      setCommentTypeFieldState({ ...commentTypeFieldState, [header.value || header.name]: v });
    } else {
      setModalFormState({ ...modalFormState, [name]: v });
      setModifiedFields({ ...modifiedFields, [name]: v });
    }
  };

  const addCommentClick = () => { };

  const handleOnAssignedToChange = (name, value, _u, uiFieldType, header, valueObj) => {
    setOnAssignedToChangeTrack(true);
    setOnStateChangeTrack(true);
    let v = value;
    if (header?.type === "namepicker" && header.data.type === 'roles' && valueObj) v = valueObj?.value;
    if (header?.type === "comments") {
      setCommentTypeFieldState({ ...commentTypeFieldState, [header.value || header.name]: v });
    } else {
      setModalFormState({ ...modalFormState, [name]: v });
      setModifiedFields({ ...modifiedFields, [name]: v });
    }
  };

  const handleOnNextAssignedToChange = (name, value, _u, uiFieldType, header, valueObj) => {
    let v = value;
    if (header?.type === "namepicker" && header.data.type === 'roles' && valueObj) v = valueObj?.value;
    if (header?.type === "comments") {
      setCommentTypeFieldState({ ...commentTypeFieldState, [header.value || header.name]: v });
    } else {
      setModalFormState({ ...modalFormState, [name]: v });
      setModifiedFields({ ...modifiedFields, [name]: v });
    }
  };

  useEffect(() => {
    if (document?.[`${chartUniqueId}_${block.name}_Update_Task`]) {
      if (onAssignedToChangeTrack) {
        changeAssignee({
          selfAssign: false,
          documentId: selectedId,
          member: (() => {
            const found = defaultNextRespondentForward.find(
              item => item.name === modalFormState["Currentassignee"] || item.name === modalFormState["Currentassignee"]
            );
            return found;
          })(),
        });
      } else {
        props.clearReduxDataOfCurrentComponent({ key: 'document', subKey: [`${chartUniqueId}_${block.name}_Update_Task`] });
        props.clearReduxDataOfCurrentComponent({ key: 'document', subKey: [`${chartUniqueId}_Task_Assigning_Upated`] });
        props.clearReduxDataOfCurrentComponent({ key: 'document', subKey: [`${chartUniqueId}_Next_Steps_Respondent_Tasks`] });
        props.clearReduxDataOfCurrentComponent({ key: 'document', subKey: [`${chartUniqueId}_Next_Step_Tasks`] });
        props.clearReduxDataOfCurrentComponent({ key: 'document', subKey: [`${chartUniqueId}_${block.name}_All_Task_Data`] });
        onCloseModel();
        onSaveHandler();
      }
    }
  }, [document?.[`${chartUniqueId}_${block.name}_Update_Task`]]);

  const changeAssignee = (payload) => {
    let finalpayload = {
      ...payload,
      appid: props.match.params.appid,
      dataModelId: block.dataModel,
      dataUniqueId: props.data?.chartUniqueId + '_Task_Assigning_Upated',
    };
    props.changeAssigneeRequest(finalpayload);
  };

  useEffect(() => {
    if (document?.[`${chartUniqueId}_Task_Assigning_Upated`]) {
      toast.success(document?.[`${chartUniqueId}_Task_Assigning_Upated`]?.message);
      props.clearReduxDataOfCurrentComponent({ key: 'document', subKey: [`${chartUniqueId}_${block.name}_Update_Task`] });
      props.clearReduxDataOfCurrentComponent({ key: 'document', subKey: [`${chartUniqueId}_Task_Assigning_Upated`] });
      props.clearReduxDataOfCurrentComponent({ key: 'document', subKey: [`${chartUniqueId}_Next_Steps_Respondent_Tasks`] });
      props.clearReduxDataOfCurrentComponent({ key: 'document', subKey: [`${chartUniqueId}_Next_Step_Tasks`] });
      props.clearReduxDataOfCurrentComponent({ key: 'document', subKey: [`${chartUniqueId}_${block.name}_All_Task_Data`] });
      onCloseModel();
      onSaveHandler();
    }
  }, [document?.[`${chartUniqueId}_Task_Assigning_Upated`]]);

  const onSave = () => {
    let obj = JSON.parse(JSON.stringify(modalFormState));
    if (commentTypeFieldState && Object.keys(commentTypeFieldState)) {
      Object.keys(commentTypeFieldState).forEach(key => {
        let v = {
          datetime: new Date().toISOString(),
          comment: commentTypeFieldState[key],
          by: { id: members.userId, name: members.name },
          label: "",
        };
        if (obj[key]) obj[key] = [...obj[key], v];
        else obj[key] = [v];
      });
    }
    obj["obligationId"] = currentOpportunityId;
    let payload = {
      appId: props.appId,
      dataModelId: block.mainDataModel,
      subDataModelId: block.dataModel,
      obligationId: currentOpportunityId,
      data: obj,
    };
    if (modalInfo.type === 'create') {
      payload['dataUniqueId'] = `${chartUniqueId}_${block.name}_Create_Task`;
      if (obj["assignedTo"]) {
        payload["data"]["workFlowId"] = block.workFlowId;
        payload["data"]['member'] = (() => {
          const found = firstStepsMembersDropdown.find(item => item.name === modalFormState["assignedTo"]);
          return found;
        })();
      }
      props.createTaskForObligations(payload);
    } else if (modalInfo.type === 'edit') {
      payload['dataUniqueId'] = `${chartUniqueId}_${block.name}_Update_Task`;
      payload['taskId'] = selectedId;
      payload['data'] = obj ? { ...modifiedFields, ...obj } : modifiedFields;
      setOnStateChangeTrack(false);
      props.updateSingleTaskofobligation(payload);
    }
  };

  const getDataById = (id) => {
    let payload = {
      appId: appId,
      dataModelId: block.mainDataModel,
      subDataModelId: block.dataModel,
      obligationId: currentOpportunityId,
      taskId: id,
      dataUniqueId: `${chartUniqueId}_${block.name}_${id}_Task`,
    };
    props.getSingleTaskofobligation(payload);
  };

  useEffect(() => {
    if (document?.[`${chartUniqueId}_Next_Step_Tasks`]) {
      let d = document?.[`${chartUniqueId}_Next_Step_Tasks`];
      fetchNextRespondents(d?.currentPossibleAction, block?.workFlowId, modalFormState?.["workflow"]?.id);
    }
  }, [document?.[`${chartUniqueId}_Next_Step_Tasks`]]);

  useEffect(() => {
    if (document?.[`${chartUniqueId}_Next_Steps_Respondent_Tasks`]) {
      let d = document?.[`${chartUniqueId}_Next_Steps_Respondent_Tasks`];
      setDefaultNextRespondent(d);
      const formattedWorkflow = d.map(item => ({ value: item.name, label: item.name }));
      setDefaultOptionsDropdown(formattedWorkflow);
    }
  }, [document?.[`${chartUniqueId}_Next_Steps_Respondent_Tasks`]]);

  const fetchTasks = (d) => {
    if (d) {
      let payload = {
        workflowId: d?.workFlowId,
        currentTaskId: d?.workflow["currentTaskId"],
        appId: props.match.params.appid,
        dataUniqueId: chartUniqueId + '_Next_Step_Tasks',
        data: { docId: modalInfo.data?.id, dmId: block.dataModel },
      };
      props.getNextTasks(payload);
    }
  };

  const fetchNextRespondents = (obj, workflowId, currentTaskId) => {
    if (obj && Array.isArray(obj)) {
      obj.map((te) => {
        let payload = {
          workflowId,
          currentTaskId,
          appId: props.match.params.appid,
          orgId: props.user?.orgId,
          dataUniqueId: props.data.chartUniqueId + '_Next_Steps_Respondent_Tasks',
          data: {
            docId: modalInfo.data?._id,
            dmId: block.dataModel,
            nextTaskId: te.id,
          },
        };
        props.getNextRespondent(payload);
        return null;
      });
    }
  };

  const onMoveTaskNext = (event, _str, obj) => {
    if (onStateChangeTrack) {
      toast.error("You have unsaved changes. Save them before proceeding with the task");
      event.stopPropagation();
    } else {
      if (modalFormState["nextStepAssignedTo"]) {
        handleAssignMembers(onAssignedToChangeTrack, obj);
        event.stopPropagation();
        setCurrentButtonClicked(obj);
      } else {
        if (obj.type === "end") {
          handleAssignMembers(onAssignedToChangeTrack, obj);
          event.stopPropagation();
          setCurrentButtonClicked(obj);
        } else {
          toast.error("Please select the next step assignee before moving the task to the next step");
          event.stopPropagation();
        }
      }
    }
  };

  const handleAssignMembers = (_v, obj) => {
    let payload = {
      isMulti: false,
      workflowId: modalFormState?.workFlowId,
      currentTaskId: modalFormState?.["workflow"]?.id,
      appId: props.match.params.appid,
      orgId: props.user?.orgId,
      dataUniqueId: chartUniqueId + '_Tasks_Execute_Workflow',
      data: {
        docId: modalInfo.data?._id,
        dmId: block.dataModel,
        nextTaskId: obj.id,
      },
    };
    if (modalFormState?.["assignedTo"] && modalInfo.type === 'create') {
      payload.data['nextRespondent'] = (() => {
        const found = defaultNextRespondent.find(
          item => item.name === modalFormState["assignedTo"] || item.name === modalFormState["assignedTo"]
        );
        return found ? found : null;
      })();
    }
    if (modalFormState?.["nextStepAssignedTo"] && modalInfo.type === 'edit' && obj.type !== "end") {
      payload.data['nextRespondent'] = (() => {
        const found = defaultNextRespondent.find(
          item => item.name === modalFormState["nextStepAssignedTo"] || item.name === modalFormState["nextStepAssignedTo"]
        );
        return found;
      })();
    }
    if (modalFormState?.["Currentassignee"] && modalInfo.type === 'edit' && obj.type === "end") {
      payload.data['nextRespondent'] = (() => {
        const found = defaultNextRespondentForward.find(
          item => item.name === modalFormState["Currentassignee"] || item.name === modalFormState["Currentassignee"]
        );
        return found;
      })();
    }
    props.executeWorkFlow(payload);
  };

  const handleReject = () => {
    if (onStateChangeTrack) {
      toast.error("You have unsaved changes. Save them before proceeding with the task");
      // event not available here; just return
      return;
    } else {
      let payload = {
        workflowId: modalFormState?.workFlowId,
        currentTaskId: modalFormState?.["workflow"]?.id,
        appId: props.match.params.appid,
        dataUniqueId: chartUniqueId + '_Tasks_Execute_Workflow_Reject',
        data: { docId: modalInfo.data?._id, dmId: block.dataModel },
      };
      props.executeWorkFlowReject(payload);
    }
  };

  const onAssignTo = (obj) => {
    changeAssignee({
      selfAssign: false,
      documentId: modalInfo?.data?._id,
      member: (() => {
        const found = defaultNextRespondentForward.find(
          item => item.name === obj.value || item.name === obj.value
        );
        return found;
      })(),
    });
  };

  return (
    <Dialog
      open={modalInfo.enable}
      fullWidth
      maxWidth="md"
      scroll="paper"                          // let content scroll inside
      fullScreen={isXs}                       // phones: full-screen UX
      classes={{ paper: modalInfo.type === 'success' ? inputClasses.customDialogPaperSuccess : modalInfo.type === 'rejected' ? inputClasses.customDialogPaperRejected : modalInfo.type === 'forwardTo' ? inputClasses.customDialogPaperforwardTo : modalInfo.type === 'deleteTask' ? inputClasses.customDialogPaperDeleteTask : inputClasses.customDialogPaper }}
      aria-labelledby="comparision-dialog-title"
      disableEscapeKeyDown
    >
      {/* CREATE */}
      {modalInfo.type === 'create' && (
        <div>
          <DialogTitle id="customized-dialog-title">
            <Typography component="span" className={inputClasses.dialogTitleText}>
              {modalInfo.title}
            </Typography>
          </DialogTitle>
          <DialogContent dividers className={inputClasses.dialogContent}>
            {(formData[`${chartUniqueId}_${block.name}_loading`] ||
              formData[`${chartUniqueId}_${block.name}_Create_Task_loading`])
              ? loader
              : (
                <Grid container spacing={2}>
                  {formHeaders?.length > 0 ? (
                    formHeaders.map((element, index) => {
                      const sValue = Helpers.generateValueByDataElementType(modalFormState, element) || '-';
                      return (
                        <Grid
                          item
                          key={index}
                          xs={12}
                          sm={6}
                          md={element.xs || 6}
                          style={{ marginTop: 2, marginBottom: 2 }}
                        >
                          <Box className={inputClasses.boxFieldStack}>
                            <Typography component="span" className={inputClasses.labelText}>
                              {element.label} :
                            </Typography>
                            <Box className={inputClasses.mtHalf} width="100%">
                              <RenderFieldBasedOnType
                                {...props}
                                classes={classes}
                                inputClasses={inputClasses}
                                element={element}
                                onChange={handleOnChange}
                                dataObj={modalFormState}
                                commentTypeFieldState={commentTypeFieldState}
                                options={defaultOptions[element.value || element.name]}
                                masterDataStoreObject={masterDataStoreObject}
                                hidePostButton={modalInfo.type === 'edit' ? false : true}
                                addCommentClick={addCommentClick}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })
                  ) : (
                    <Typography>No Fields are Configured</Typography>
                  )}

                  {!formData[`${chartUniqueId}_${block.name}_loading`] && formHeaders?.length > 0 && modalInfo.type === 'create' && (
                    <Grid item xs={12} sm={6} md={6} style={{ marginTop: 2, marginBottom: 2 }}>
                      <Box className={inputClasses.boxFieldStack}>
                        <Typography component="span" className={inputClasses.labelText}>
                          Assigned To :
                        </Typography>
                        <Box className={inputClasses.mtHalf} width="100%">
                          <RenderFieldBasedOnType
                            {...props}
                            classes={classes}
                            inputClasses={inputClasses}
                            element={{ value: "assignedTo", label: "assignedTo", data: { type: "roles" }, type: "namepicker", ...(modalInfo?.type === 'view' && { readOnly: true }) }}
                            onChange={handleOnAssignedToChange}
                            dataObj={modalFormState}
                            commentTypeFieldState={commentTypeFieldState}
                            options={defaultOptionsDropdown}
                            masterDataStoreObject={masterDataStoreObject}
                            hidePostButton={modalInfo.type === 'edit' ? false : true}
                            addCommentClick={addCommentClick}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              )}
          </DialogContent>
          <DialogActions>
            <Button color="inherit" size="small" onClick={onCloseModel}>
              Close
            </Button>
            <Button className={`${inputClasses.btnPrimary} ${inputClasses.btnSmallTall}`} size="small" onClick={onSave}>
              Create Task
            </Button>
          </DialogActions>
        </div>
      )}

      {/* EDIT */}
      {modalInfo.type === 'edit' && (
        <div>
          <DialogTitle id="customized-dialog-title">
            <Typography component="span" className={inputClasses.dialogTitleText}>
              {modalInfo.title}
            </Typography>
          </DialogTitle>
          <DialogContent dividers className={inputClasses.dialogContent}>
            {(formData[`${chartUniqueId}_${block.name}_loading`] ||
              formData[`${chartUniqueId}_${block.name}_Update_Task_loading`] ||
              formData[`${chartUniqueId}_${block.name}_Tasks_Execute_Workflow`] ||
              formData[`${chartUniqueId}_${block.name}_${modalInfo.data._id}_Task_loading`])
              ? loader
              : (
                <Grid>
                  {/* Tabs */}
                  <div className={inputClasses.tabsBar}>
                    <div
                      className={`${inputClasses.tab} ${activeTabMain === "taskDetails" ? inputClasses.tabActive : ""}`}
                      onClick={() => setActiveTabMain("taskDetails")}
                    >
                      <Typography
                        className={`${inputClasses.tabText} ${activeTabMain === "taskDetails" ? inputClasses.tabTextActive : ""}`}
                      >
                        Task Details
                      </Typography>
                    </div>
                    <div
                      className={`${inputClasses.tab} ${activeTabMain === "auditTrails" ? inputClasses.tabActive : ""}`}
                      onClick={() => setActiveTabMain("auditTrails")}
                    >
                      <Typography
                        className={`${inputClasses.tabText} ${activeTabMain === "auditTrails" ? inputClasses.tabTextActive : ""}`}
                      >
                        Audit Trails
                      </Typography>
                    </div>
                  </div>

                  {/* Task Details */}
                  {activeTabMain === "taskDetails" && (
                    <Grid container spacing={2} className={inputClasses.mt10}>
                      {formHeaders?.length > 0 ? (
                        formHeaders.map((element, index) => (
                          <Grid
                            item
                            key={index}
                            xs={12}
                            sm={6}
                            md={element.xs || 6}
                            style={{ marginTop: 2, marginBottom: 2 }}
                          >
                            <Box className={inputClasses.boxFieldStack}>
                              <Typography component="span" className={inputClasses.labelText}>
                                {element.label} :
                              </Typography>
                              <Box className={inputClasses.mtHalf} width="100%">
                                <RenderFieldBasedOnType
                                  {...props}
                                  classes={classes}
                                  inputClasses={inputClasses}
                                  element={element}
                                  onChange={handleOnChange}
                                  dataObj={modalFormState}
                                  commentTypeFieldState={commentTypeFieldState}
                                  options={defaultOptions[element.value || element.name]}
                                  masterDataStoreObject={masterDataStoreObject}
                                  hidePostButton={modalInfo.type === 'edit' ? false : true}
                                  addCommentClick={addCommentClick}
                                />
                              </Box>
                            </Box>
                          </Grid>
                        ))
                      ) : (
                        <Typography>No Fields are Configured</Typography>
                      )}

                      {/* Status (read-only) */}
                      {!formData[`${chartUniqueId}_${block.name}_loading`] && formHeaders?.length > 0 && modalInfo.type === 'edit' && (
                        <Grid item xs={12} sm={6} md={6} style={{ marginTop: 2, marginBottom: 2 }}>
                          <Box className={inputClasses.boxFieldStack}>
                            <Typography component="span" className={inputClasses.labelText}>
                              Status :
                            </Typography>
                            <Box className={inputClasses.mtHalf} width="100%">
                              <RenderFieldBasedOnType
                                {...props}
                                classes={classes}
                                inputClasses={inputClasses}
                                element={{ value: "status", label: "status", type: "string", ...(modalInfo?.type === 'edit' && { readOnly: true }) }}
                                onChange={handleOnAssignedToChange}
                                dataObj={modalFormState}
                                commentTypeFieldState={commentTypeFieldState}
                                options={defaultOptionsDropdown}
                                masterDataStoreObject={masterDataStoreObject}
                                hidePostButton={modalInfo.type === 'edit' ? false : true}
                                addCommentClick={addCommentClick}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* Current Step Assignee */}
                      {!formData[`${chartUniqueId}_${block.name}_loading`] && formHeaders?.length > 0 && modalInfo.type === 'edit' && (
                        <Grid item xs={12} sm={6} md={6} style={{ marginTop: 2, marginBottom: 2 }}>
                          <Box className={inputClasses.boxFieldStack}>
                            <Typography component="span" className={inputClasses.labelText}>
                              Current Step Assignee :
                            </Typography>
                            <Box className={inputClasses.mtHalf} width="100%">
                              <RenderFieldBasedOnType
                                {...props}
                                classes={classes}
                                inputClasses={inputClasses}
                                element={{ value: "Currentassignee", label: "Currentassignee", data: { type: "roles" }, type: "namepicker", ...(modalInfo?.type === 'view' && { readOnly: true }) }}
                                onChange={handleOnAssignedToChange}
                                dataObj={modalFormState}
                                commentTypeFieldState={commentTypeFieldState}
                                options={defaultOptionsDropdownForward}
                                masterDataStoreObject={masterDataStoreObject}
                                hidePostButton={modalInfo.type === 'edit' ? false : true}
                                addCommentClick={addCommentClick}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* Next Step Assigned To (if end) */}
                      {!formData[`${chartUniqueId}_${block.name}_loading`] && formHeaders?.length > 0 && modalInfo.type === 'edit' && !isNextStepEnd && (
                        <Grid item xs={12} sm={6} md={6} style={{ marginTop: 2, marginBottom: 2 }}>
                          <Box className={inputClasses.boxFieldStack}>
                            <Typography component="span" className={inputClasses.labelText}>
                              Next Step Assigned To :
                            </Typography>
                            <Box className={inputClasses.mtHalf} width="100%">
                              <RenderFieldBasedOnType
                                {...props}
                                classes={classes}
                                inputClasses={inputClasses}
                                element={{ value: "nextStepAssignedTo", label: "nextStepAssignedTo", data: { type: "roles" }, type: "namepicker", ...(modalInfo?.type === 'view' && { readOnly: true }) }}
                                onChange={handleOnNextAssignedToChange}
                                dataObj={modalFormState}
                                commentTypeFieldState={commentTypeFieldState}
                                options={defaultOptionsDropdown}
                                masterDataStoreObject={masterDataStoreObject}
                                hidePostButton={modalInfo.type === 'edit' ? false : true}
                                addCommentClick={addCommentClick}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* Owner (read-only) */}
                      {!formData[`${chartUniqueId}_${block.name}_loading`] && formHeaders?.length > 0 && modalInfo.type === 'edit' && (
                        <Grid item xs={12} sm={6} md={6} style={{ marginTop: 2, marginBottom: 2 }}>
                          <Box className={inputClasses.boxFieldStack}>
                            <Typography component="span" className={inputClasses.labelText}>
                              Owner :
                            </Typography>
                            <Box className={inputClasses.mtHalf} width="100%">
                              <RenderFieldBasedOnType
                                {...props}
                                classes={classes}
                                inputClasses={inputClasses}
                                element={{ value: "owner", label: "owner", type: "string", ...(modalInfo?.type === 'edit' && { readOnly: true }) }}
                                onChange={handleOnAssignedToChange}
                                dataObj={modalFormState}
                                commentTypeFieldState={commentTypeFieldState}
                                options={defaultOptionsDropdown}
                                masterDataStoreObject={masterDataStoreObject}
                                hidePostButton={modalInfo.type === 'edit' ? false : true}
                                addCommentClick={addCommentClick}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {modalFormState?.["workflow"] && (
                        props.formData?.document[`${props.data.chartUniqueId}_Add_Comment_Task_Obligation_loading`]
                          ? props.loader
                          : (
                            <Grid item xs={12} style={{ marginTop: 2, marginBottom: 2 }}>
                              <TaskCommentViewer
                                {...props}
                                show
                                block={block}
                                selectedId={selectedId}
                                modalFormState={modalFormState}
                                getDataById={getDataById}
                              />
                            </Grid>
                          )
                      )}
                    </Grid>
                  )}

                  {/* Audit Trails */}
                  {activeTabMain === "auditTrails" && (
                    <div className={`${inputClasses.auditScroll}`}>
                      {props.formData?.[chartUniqueId + '_audit_Trails_Task_Obligations_loading']
                        ? props.loader
                        : auditTrail && auditTrail.map((res, index) => {
                          const iconSrc = getOperationIcon(res.formName, res.operation, res.formType) || '/media/svg/tickObligations.svg';
                          return (
                            <div key={index} className={inputClasses.auditRow}>
                              <div className={inputClasses.auditDateCol}>
                                <Typography className={inputClasses.auditDateText}>
                                  {formatDate(res["createdDate"])}
                                </Typography>
                              </div>
                              <div className={inputClasses.auditIconCol}>
                                <div className={inputClasses.icon20Btn}>
                                  <img src={iconSrc} alt={iconSrc} width="18" height="18" />
                                </div>
                              </div>
                              <div className={inputClasses.auditMsgCol}>
                                <Typography className={inputClasses.auditMessage}>
                                  {res["message"]}
                                </Typography>
                                <Typography className={inputClasses.auditBy}>
                                  {`By ${res["userName"]}`}
                                </Typography>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </Grid>
              )}
          </DialogContent>
          <DialogActions>
            <Button color="inherit" size="small" onClick={onCloseModel}>
              Close
            </Button>

            {(onStateChangeTrack || onAssignedToChangeTrack) && modalInfo.type === 'edit' && (
              <Button
                className={`${inputClasses.btnPrimary} ${inputClasses.btnSmallCompact}`}
                onClick={onSave}
              >
                {formData[`${chartUniqueId}_${block.name}_update_loading`]
                  ? <CircularProgress color="#fff" size={24} />
                  : 'Update Task'}
              </Button>
            )}

            {onActions && modalFormState.workflow?.type !== "start" && modalFormState.workflow?.type !== "end" && modalInfo.type === 'edit' && (
              <Grid item xs={4} style={{ maxWidth: 'fit-content' }}>
                <Button
                  className={`${inputClasses.btnDanger} ${inputClasses.btnSmallCompact}`}
                  onClick={handleReject}
                >
                  Reject
                </Button>
              </Grid>
            )}

            {modalInfo.type === 'edit' &&
              modalFormState?.["workflow"] &&
              Boolean(Object.keys(formData?.document?.[chartUniqueId + '_Next_Step_Tasks'] || {}).length) &&
              Array.isArray(formData?.document?.[chartUniqueId + '_Next_Step_Tasks']?.currentPossibleAction) &&
              formData?.document?.[chartUniqueId + '_Next_Step_Tasks']?.currentPossibleAction.map((cpa, index) => (
                <Grid item key={cpa?.id || index} style={{ display: 'flex', justifyContent: 'end', gap: 10 }}>
                  {Object.keys(cpa || {}).length > 0 && (
                    <div>
                      <Button
                        className={`${inputClasses.btnPrimary} ${inputClasses.btnSmallCompact}`}
                        onClick={(e) => onMoveTaskNext(e, 'current', cpa)}
                      >
                        {cpa?.type === 'end' ? cpa?.label : `Move to ${cpa?.label}`}
                      </Button>
                    </div>
                  )}
                </Grid>
              ))
            }
          </DialogActions>
        </div>
      )}

      {/* VIEW */}
      {modalInfo.type === 'view' && (
        <div>
          <DialogTitle id="customized-dialog-title">
            <Typography component="span" className={inputClasses.dialogTitleText}>
              {modalInfo.title}
            </Typography>
          </DialogTitle>
          <DialogContent dividers className={inputClasses.dialogContent}>
            {(formData[`${chartUniqueId}_${block.name}_loading`] ||
              formData[`${chartUniqueId}_${block.name}_${modalInfo.data._id}_Task_loading`])
              ? loader
              : (
                <Grid>
                  {/* Tabs */}
                  <div className={inputClasses.tabsBar}>
                    <div
                      className={`${inputClasses.tab} ${activeTabMain === "taskDetails" ? inputClasses.tabActive : ""}`}
                      onClick={() => setActiveTabMain("taskDetails")}
                    >
                      <Typography
                        className={`${inputClasses.tabText} ${activeTabMain === "taskDetails" ? inputClasses.tabTextActive : ""}`}
                      >
                        Task Details
                      </Typography>
                    </div>
                    <div
                      className={`${inputClasses.tab} ${activeTabMain === "auditTrails" ? inputClasses.tabActive : ""}`}
                      onClick={() => setActiveTabMain("auditTrails")}
                    >
                      <Typography
                        className={`${inputClasses.tabText} ${activeTabMain === "auditTrails" ? inputClasses.tabTextActive : ""}`}
                      >
                        Audit Trails
                      </Typography>
                    </div>
                  </div>

                  {activeTabMain === "taskDetails" && (
                    <Grid container spacing={2} className={inputClasses.mt10}>
                      {formHeaders?.length > 0 ? (
                        formHeaders.map((element, index) => (
                          <Grid
                            item
                            key={index}
                            xs={12}
                            sm={6}
                            md={element.xs || 6}
                            style={{ marginTop: 2, marginBottom: 2 }}
                          >
                            <Box className={inputClasses.boxFieldStack}>
                              <Typography component="span" className={inputClasses.labelText}>
                                {element.label} :
                              </Typography>
                              <Box className={inputClasses.mtHalf} width="100%">
                                <RenderFieldBasedOnType
                                  {...props}
                                  classes={classes}
                                  inputClasses={inputClasses}
                                  element={{ ...element, ...(modalInfo?.data?.onlyViewIt ? { readOnly: true } : {}) }}
                                  onChange={handleOnChange}
                                  dataObj={modalFormState}
                                  commentTypeFieldState={commentTypeFieldState}
                                  options={defaultOptions[element.value || element.name]}
                                  masterDataStoreObject={masterDataStoreObject}
                                  hidePostButton={modalInfo.type === 'edit' ? false : true}
                                  addCommentClick={addCommentClick}
                                />
                              </Box>
                            </Box>
                          </Grid>
                        ))
                      ) : (
                        <Typography>No Fields are Configured</Typography>
                      )}

                      {/* Assigned To */}
                      {!formData[`${chartUniqueId}_${block.name}_loading`] && formHeaders?.length > 0 && modalInfo.type === 'view' && (
                        <Grid item xs={12} sm={6} md={6} style={{ marginTop: 2, marginBottom: 2 }}>
                          <Box className={inputClasses.boxFieldStack}>
                            <Typography component="span" className={inputClasses.labelText}>
                              Assigned To :
                            </Typography>
                            <Box className={inputClasses.mtHalf} width="100%">
                              <RenderFieldBasedOnType
                                {...props}
                                classes={classes}
                                inputClasses={inputClasses}
                                element={{ value: "Currentassignee", label: "Currentassignee", data: { type: "roles" }, type: "namepicker", ...(modalInfo?.type === 'view' && { readOnly: true }) }}
                                onChange={handleOnAssignedToChange}
                                dataObj={modalFormState}
                                commentTypeFieldState={commentTypeFieldState}
                                options={defaultOptionsDropdown}
                                masterDataStoreObject={masterDataStoreObject}
                                hidePostButton={modalInfo.type === 'edit' ? false : true}
                                addCommentClick={addCommentClick}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* Status */}
                      {!formData[`${chartUniqueId}_${block.name}_loading`] && formHeaders?.length > 0 && modalInfo.type === 'view' && (
                        <Grid item xs={12} sm={6} md={6} style={{ marginTop: 2, marginBottom: 2 }}>
                          <Box className={inputClasses.boxFieldStack}>
                            <Typography component="span" className={inputClasses.labelText}>
                              Status :
                            </Typography>
                            <Box className={inputClasses.mtHalf} width="100%">
                              <RenderFieldBasedOnType
                                {...props}
                                classes={classes}
                                inputClasses={inputClasses}
                                element={{ value: "status", label: "status", type: "string", ...(modalInfo?.type === 'view' && { readOnly: true }) }}
                                onChange={handleOnAssignedToChange}
                                dataObj={modalFormState}
                                commentTypeFieldState={commentTypeFieldState}
                                options={defaultOptionsDropdown}
                                masterDataStoreObject={masterDataStoreObject}
                                hidePostButton={modalInfo.type === 'edit' ? false : true}
                                addCommentClick={addCommentClick}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* Owner */}
                      {!formData[`${chartUniqueId}_${block.name}_loading`] && formHeaders?.length > 0 && modalInfo.type === 'view' && (
                        <Grid item xs={12} sm={6} md={6} style={{ marginTop: 2, marginBottom: 2 }}>
                          <Box className={inputClasses.boxFieldStack}>
                            <Typography component="span" className={inputClasses.labelText}>
                              Owner :
                            </Typography>
                            <Box className={inputClasses.mtHalf} width="100%">
                              <RenderFieldBasedOnType
                                {...props}
                                classes={classes}
                                inputClasses={inputClasses}
                                element={{ value: "owner", label: "owner", type: "string", ...(modalInfo?.type === 'view' && { readOnly: true }) }}
                                onChange={handleOnAssignedToChange}
                                dataObj={modalFormState}
                                commentTypeFieldState={commentTypeFieldState}
                                options={defaultOptionsDropdown}
                                masterDataStoreObject={masterDataStoreObject}
                                hidePostButton={modalInfo.type === 'edit' ? false : true}
                                addCommentClick={addCommentClick}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {modalFormState?.["workflow"] && (
                        props.formData?.document[`${props.data.chartUniqueId}_Add_Comment_Task_Obligation_loading`]
                          ? props.loader
                          : (
                            <Grid item xs={12} style={{ marginTop: 2, marginBottom: 2 }}>
                              <TaskCommentViewer
                                {...props}
                                show={false}
                                block={block}
                                selectedId={selectedId}
                                modalFormState={modalFormState}
                              />
                            </Grid>
                          )
                      )}
                    </Grid>
                  )}

                  {activeTabMain === "auditTrails" && (
                    <div className={inputClasses.auditScroll}>
                      {props.formData?.[chartUniqueId + '_audit_Trails_Task_Obligations_loading']
                        ? props.loader
                        : auditTrail && auditTrail.map((res, index) => {
                          const iconSrc = getOperationIcon(res.formName, res.operation, res.formType) || '/media/svg/tickObligations.svg';
                          return (
                            <div key={index} className={inputClasses.auditRow}>
                              <div className={inputClasses.auditDateCol}>
                                <Typography className={inputClasses.auditDateText}>
                                  {formatDate(res["createdDate"])}
                                </Typography>
                              </div>
                              <div className={inputClasses.auditIconCol}>
                                <div className={inputClasses.icon20Btn}>
                                  <img src={iconSrc} alt={iconSrc} width="18" height="18" />
                                </div>
                              </div>
                              <div className={inputClasses.auditMsgCol}>
                                <Typography className={inputClasses.auditMessage}>
                                  {res["message"]}
                                </Typography>
                                <Typography className={inputClasses.auditBy}>
                                  {`By ${res["userName"]}`}
                                </Typography>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </Grid>
              )}
          </DialogContent>
          <DialogActions>
            <Button color="inherit" size="small" onClick={onCloseModel}>
              Close
            </Button>

            {(onStateChangeTrack || onAssignedToChangeTrack) && modalInfo.type === 'edit' && (
              <Button
                className={`${inputClasses.btnPrimary} ${inputClasses.btnSmallTall}`}
                onClick={onSave}
              >
                {formData[`${chartUniqueId}_${block.name}_update_loading`]
                  ? <CircularProgress color="#fff" size={24} />
                  : 'Update Task'}
              </Button>
            )}

            {onActions && modalFormState.workflow?.type !== "start" && modalFormState.workflow?.type !== "end" && modalInfo.type === 'edit' && (
              <Grid item xs={4} style={{ maxWidth: 'fit-content' }}>
                <Button
                  className={`${inputClasses.btnDanger} ${inputClasses.btnSmallTall}`}
                  onClick={handleReject}
                >
                  Reject
                </Button>
              </Grid>
            )}

            {modalInfo.type === 'edit' &&
              modalFormState?.["workflow"] &&
              Boolean(Object.keys(formData?.document?.[chartUniqueId + '_Next_Step_Tasks'] || {}).length) &&
              Array.isArray(formData?.document?.[chartUniqueId + '_Next_Step_Tasks']?.currentPossibleAction) &&
              formData?.document?.[chartUniqueId + '_Next_Step_Tasks']?.currentPossibleAction.map((cpa, index) => (
                <Grid item key={cpa?.id || index} style={{ display: 'flex', justifyContent: 'end', gap: 10 }}>
                  {Object.keys(cpa || {}).length > 0 && (
                    <div>
                      <Button
                        className={`${inputClasses.btnPrimary} ${inputClasses.btnSmallTall}`}
                        onClick={(e) => onMoveTaskNext(e, 'current', cpa)}
                      >
                        {`Move to ${cpa?.label}`}
                      </Button>
                    </div>
                  )}
                </Grid>
              ))
            }
          </DialogActions>
        </div>
      )}

      {/* SUCCESS */}
      {modalInfo.type === 'success' && (
        <div>
          <DialogContent className={inputClasses.dialogContent}>
            <div className={inputClasses.dialogCenterWrap}>
              <div className={inputClasses.centerRow}>
                <img src="/media/svg/moveNext.svg" alt="Move Next" className={inputClasses.icon48} />
              </div>
              <div className={`${inputClasses.col} ${inputClasses.gap4}`}>
                <div className={inputClasses.centerRow}>
                  <Typography component="span" className={inputClasses.infoText}>
                    Successfully Moved
                  </Typography>
                </div>
                <div className={inputClasses.centerRow}>
                  <Typography component="span" className={inputClasses.centerSubText}>
                    {modalInfo.message}
                  </Typography>
                </div>
              </div>
              <div className={inputClasses.centerRow}>
                <Button className={`${inputClasses.btnSuccess} ${inputClasses.btnSmallTall}`} size="small" onClick={props.onSucessClick}>
                  OKAY
                </Button>
              </div>
            </div>
          </DialogContent>
        </div>
      )}

      {/* REJECTED */}
      {modalInfo.type === 'rejected' && (
        <div>
          <DialogContent className={inputClasses.dialogContent}>
            <div className={inputClasses.dialogCenterWrap}>
              <div className={inputClasses.centerRow}>
                <img src="/media/svg/movePrevious.svg" alt="Move Previous" className={inputClasses.icon48} />
              </div>
              <div className={`${inputClasses.col} ${inputClasses.gap4}`}>
                <div className={inputClasses.centerRow}>
                  <Typography component="span" className={inputClasses.infoText}>
                    Rejected
                  </Typography>
                </div>
                <div className={inputClasses.centerRow}>
                  <Typography component="span" className={inputClasses.centerSubText}>
                    The document has been Rejected moved to the implementation process
                  </Typography>
                </div>
              </div>
              <div className={inputClasses.centerRow}>
                <Button className={`${inputClasses.btnSuccess} ${inputClasses.btnSmallTall}`} size="small" onClick={props.onRejectedModel}>
                  OKAY
                </Button>
              </div>
            </div>
          </DialogContent>
        </div>
      )}

      {/* DELETE TASK */}
      {modalInfo.type === 'deleteTask' && (
        <div>
          <DialogContent className={inputClasses.dialogContent}>
            <div className={inputClasses.dialogCenterWrap}>
              <div className={inputClasses.centerRow}>
                <img src="/media/svg/deleteTaskConfimation.svg" alt="Delete Task Confimation" className={inputClasses.icon48} />
              </div>
              <div className={inputClasses.col}>
                <div className={inputClasses.centerRow}>
                  <Typography component="span" className={inputClasses.labelText} style={{ fontSize: 14 }}>
                    Delete Confirmation
                  </Typography>
                </div>
                <div className={inputClasses.centerRow}>
                  <Typography component="span" className={inputClasses.centerSubText}>
                    Are you sure you want to delete this task?
                  </Typography>
                </div>
              </div>
              <div className={`${inputClasses.centerRow} ${inputClasses.gap12}`}>
                <Button
                  variant="outlined"
                  className={inputClasses.btnOutlinedNeutral}
                  size="small"
                  onClick={onCloseModel}
                >
                  CANCEL
                </Button>
                <Button
                  className={`${inputClasses.btnSuccess}`}
                  size="small"
                  onClick={() => { onDeleteTask(modalInfo?.data?._id); }}
                >
                  YES DELETE
                </Button>
              </div>
            </div>
          </DialogContent>
        </div>
      )}

      {/* FORWARD TO */}
      {modalInfo.type === 'forwardTo' && (
        <div>
          <DialogTitle id="customized-dialog-title">
            <Typography component="span" className={inputClasses.dialogTitleText}>
              {modalInfo.title}
            </Typography>
          </DialogTitle>
          <DialogContent dividers className={inputClasses.dialogContent}>
            <div className={inputClasses.col}>
              {(props.formData?.[props.data.chartUniqueId + '_Next_Steps_Respondent_Tasks_loading'] ||
                props.formData?.[`${chartUniqueId}_${block.name}_${modalInfo?.data?._id}_Task_loading`] ||
                props.formData?.[`${chartUniqueId}_Next_Step_Tasks_loading`])
                ? props.loader
                : defaultOptionsDropdownForward && defaultOptionsDropdownForward.map((data) => (
                  <div key={data.value} className={inputClasses.rowStartCenter} style={{ gap: 8 }}>
                    <Checkbox
                      checked={selectedAssigneeId?.value === data.value}
                      onChange={() => setSelectedAssigneeId(data)}
                      color="primary"
                      inputProps={{ 'aria-label': `checkbox-${data.value}` }}
                    />
                    <div className={inputClasses.rowStartCenter} style={{ gap: 8 }}>
                      <Avatar className={inputClasses.commentAvatar}>
                        {getInitials(data.label)}
                      </Avatar>
                      <Typography component="span" className={inputClasses.infoText}>
                        {data.label}
                      </Typography>
                    </div>
                  </div>
                ))
              }
            </div>
          </DialogContent>
          <DialogActions>
            <Button color="inherit" size="small" onClick={onCloseModel}>
              Close
            </Button>
            <Button
              className={`${inputClasses.btnPrimary} ${inputClasses.btnSmallTall}`}
              size="small"
              onClick={() => { if (selectedAssigneeId) onAssignTo(selectedAssigneeId); }}
            >
              Assign
            </Button>
          </DialogActions>
        </div>
      )}
    </Dialog>
  );
};

export default SubDataModelModal;

